import { ActionIcon, Button } from '@mantine/core';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import { IconMoodSmile } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmojiPicker } from 'src/Components/EmojiPicker';
import { FormAttachmentViewer } from 'src/Components/Forms/FormAttachmentViewer';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { MentionTextarea } from 'src/Components/MentionTextarea';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { useCreateCommunityFeedItem } from 'src/Hooks/Mutations/useCreateCommunityFeedItem';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { DEFAULT_UPLOAD_FOLDER, useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { useSelectedAssetId } from 'src/Hooks/Shared/useSelectedAssetId';
import { CreateCommunityFeedItemDtoTypeEnum } from 'src/Services/API/generated';

import styles from './CommunityCreateFeedItem.module.scss';

const CommunityCreateFeedItem = () => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const assetId = useSelectedAssetId();

  const [isEmojiPickerOpen, { close: closeEmojiPicker, toggle: toggleEmojiPicker }] =
    useDisclosure(false);
  const clickOutsideRef = useClickOutside(closeEmojiPicker);

  const onEmoji = (emoji: string) => {
    setText(t => t + emoji);
  };
  const isMobile = useIsMobile();
  const showEmojiPicker = !isMobile;

  // La page est mono-bien : on publie directement sur le bien courant, sans passer
  // par une modale de choix de biens.
  const { mutate: createFeedItem, isLoading: isPublishing } = useCreateCommunityFeedItem({
    onSuccess: () => setText(''),
  });

  const [attachments, setAttachments] = useState<FormAttachment[]>([]);
  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  /** Joindre un document déjà rangé dans Documents, sans le téléverser à nouveau. */
  const handleExistingFilesSelect = async (fileIds: string[]) => {
    const existing = await Promise.all(
      fileIds.map(async fileId => {
        const file = await fetchFileInfo(fileId);
        return {
          fileId: file.id,
          name: file.name,
          previewUrl: file.urls?.previewThumbnailUrl,
          type: file.type,
          video: file.video,
        };
      })
    );
    setAttachments(current => [...current, ...existing]);
  };
  const {
    uploads,
    startUpload,
    removeUpload,
    isLoading: isUploadLoading,
  } = useFileUploadManager({
    onUploadReady: readyFile => {
      if (!readyFile.fileData) return;
      setAttachments(current => [
        ...current,
        {
          video: readyFile.videoData,
          fileId: readyFile.fileData!.id,
          type: readyFile.fileData!.type,
          name: readyFile.fileData!.name,
          previewUrl: readyFile.fileData!.urls?.previewThumbnailUrl,
        },
      ]);
    },
  });

  const publish = () => {
    if (!text.trim() || !assetId) return;

    createFeedItem({
      type: CreateCommunityFeedItemDtoTypeEnum.PostRegular,
      message: text.trim(),
      files: [],
      // Documents joints au message, distincts des photos d'un album.
      attachmentIds: attachments.map(attachment => attachment.fileId).filter(Boolean) as string[],
      assets: [assetId],
    });
    setAttachments([]);
    closeEmojiPicker();
  };

  return (
    <div className={styles.container} ref={clickOutsideRef}>
      <div className={styles.inputRow}>
        <UserAvatar size={36} />

        <div className={styles.field}>
          <MentionTextarea
            value={text}
            onChange={setText}
            placeholder={t(
              'community.createPost.placeholder',
              'Quelles sont les nouvelles du jour ?'
            )}
          />
        </div>
      </div>

      {(attachments.length > 0 || uploads.length > 0) && (
        <div className={styles.attachments}>
          <FormAttachmentViewer
            attachments={attachments}
            uploadingAttachments={uploads}
            onDeleteAttachment={attachment =>
              setAttachments(current => current.filter(a => a.fileId !== attachment.fileId))
            }
            onDeleteUploadingAttachment={removeUpload}
          />
        </div>
      )}

      <div className={styles.actions}>
        <div className={styles.tools}>
          {showEmojiPicker ? (
            <ActionIcon onClick={toggleEmojiPicker} variant="outline" className={styles.icon}>
              <IconMoodSmile size={20} />
            </ActionIcon>
          ) : null}

          {/* Icone seule a toutes les largeurs : l'infobulle porte le libelle complet. */}
          <UploadFileButton
            iconOnly
            onFilesSelected={(files: File[]) =>
              startUpload(files, DEFAULT_UPLOAD_FOLDER, 'COMMUNITY_ATTACHMENT')
            }
            onExistingFilesSelect={handleExistingFilesSelect}
            loading={isUploadLoading}
            modalTitle={t('community.createPost.attachments', 'Joindre un document')}
          />
        </div>

        <Button
          disabled={text.trim().length === 0 || !assetId}
          loading={isPublishing}
          variant="outline"
          onClick={publish}
        >
          {t('community.createPost.post', 'Poster')}
        </Button>
      </div>

      <div className={styles.emojiPicker}>
        <EmojiPicker visible={isEmojiPickerOpen} onEmoji={onEmoji} />
      </div>
    </div>
  );
};

export default CommunityCreateFeedItem;
